/**
 * RealisticMapView — viewport 3D ultrarrealista basado en Google
 * Photorealistic 3D Tiles vía el Custom Element `<gmp-map-3d>`.
 *
 * Contrato similar al de `<MapView>` (`center [lng, lat]`, `zoom`, `bearing`,
 * `pitch`) pero las props internas del Map3DElement son distintas: `center`
 * va como `{lat, lng, altitude}`, `zoom` se convierte a `range` (metros de
 * la cámara al centro), `pitch`/`bearing` mapean a `tilt`/`heading`.
 *
 * MVP sin markers ni overlays:
 *  - Los 79 cabinets, 164 señales OSM y demás markers se mantienen en la
 *    vista operativa (`MapView`). En la vista realista sólo se muestra el
 *    terreno fotogramétrico — es modo "wow" para demos, no para operar.
 *  - El HUD del padre (`SantaColomaMapView`) — alarmas, controles, paneles
 *    — sigue superpuesto y funcionando sin cambios.
 *  - Iteración futura (opción C-1 del informe del agente): proyectar los
 *    CabinetMarker como overlay DOM sobre el canvas 3D si se necesita
 *    referencia espacial de los reguladores.
 *
 * Coste / fallback:
 *  - El SDK se carga la primera vez que se monta el componente — la
 *    facturación de "sesión" arranca en ese momento. Coste actual público:
 *    ~$5 / 1000 sesiones Photorealistic 3D Tiles.
 *  - Si la API falla (red, key inválida, cuota agotada) muestra un mensaje
 *    pidiendo volver a la vista operativa. No degrada al MapLibre
 *    automáticamente para que el admin sepa que algo no va.
 */
import { useEffect, useRef } from 'react';
import { useGoogleMaps3D } from './useGoogleMaps3D';
import { useCameraControl } from '../../store/cameraControl';

const _cameraToken = import.meta.env.VITE_CAMERA_TOKEN || '';
const _baseWs = import.meta.env.DEV
  ? 'ws://localhost:8310/ws/camera'
  : `wss://${window.location.host}/camera-ws`;
const CAMERA_WS_URL = _cameraToken ? `${_baseWs}?token=${_cameraToken}` : _baseWs;

interface RealisticMapViewProps {
  center: [number, number]; // [lng, lat] — mismo orden que MapView
  zoom: number;
  bearing: number;
  pitch: number;
  /** altitud del centro en metros sobre WGS84 (default 50, ajustable). */
  altitude?: number;
  /**
   * Mismo contrato que `MapView.onViewState`: se llama cuando el usuario
   * mueve la cámara con interacción 3D nativa. Permite que el overlay
   * debug del padre y la detección de cruce cercano funcionen igual en
   * ambos modos.
   */
  onViewState?: (center: [number, number], zoom: number, bearing: number, pitch: number) => void;
  /**
   * Se llama una vez con el `Map3DElement` listo. Permite al padre montar
   * markers / polylines nativos (`Marker3DInteractiveElement`, etc.) sin
   * que este wrapper sepa del dominio. Devuelve un cleanup que se ejecuta
   * al desmontar el componente.
   */
  onMapReady?: (mapEl: HTMLElement) => (() => void) | void;
}

/**
 * Conversión MapLibre zoom ↔ Map3DElement range (metros).
 *
 * La constante Web Mercator estándar (591657550) supone vista cenital
 * pura. Map3DElement usa tilt 60-67° y "range" es la distancia diagonal
 * cámara→target, no la altura. Con la constante estándar la inversa
 * sobreestima el zoom equivalente ~3-4 niveles → al pasar de Realista
 * a MapLibre con el mismo center, MapLibre aterrizaba a vista de calle
 * individual aunque la Realista mostrara el municipio entero.
 *
 * Calibración empírica para Santa Coloma con tilt ~60-68°:
 *   - range 800 m  ↔ zoom 15.5 (vista barrio)
 *   - range 2000 m ↔ zoom 14   (municipio)
 *   - range 300 m  ↔ zoom 17   (cruce)
 *
 * La constante ajustada (50M) se obtuvo de despejar la fórmula
 * para los pares (range, zoom) deseados.
 */
const ZOOM_RANGE_CONST = 50_000_000;

export function zoomToRange(zoom: number, lat: number): number {
  const meters = (ZOOM_RANGE_CONST / Math.pow(2, zoom)) * Math.cos((lat * Math.PI) / 180);
  // Map3D acepta range ≥ 0; con zoom >19 saldría < 1 m, lo capamos para no
  // meternos dentro del modelo.
  return Math.max(meters, 50);
}

export function rangeToZoom(range: number, lat: number): number {
  const cosLat = Math.cos((lat * Math.PI) / 180);
  if (range <= 0 || cosLat <= 0) return 0;
  return Math.log2((ZOOM_RANGE_CONST * cosLat) / range);
}

export function RealisticMapView({
  center,
  zoom,
  bearing,
  pitch,
  altitude = 50,
  onViewState,
  onMapReady,
}: RealisticMapViewProps) {
  const { loaded, error } = useGoogleMaps3D();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<HTMLElement | null>(null);
  const onViewStateRef = useRef(onViewState);
  const onMapReadyRef = useRef(onMapReady);
  useEffect(() => { onViewStateRef.current = onViewState; }, [onViewState]);
  useEffect(() => { onMapReadyRef.current = onMapReady; }, [onMapReady]);

  // Crear el Map3DElement una vez que el SDK está listo
  useEffect(() => {
    if (!loaded || !containerRef.current || mapRef.current) return;

    const Map3DElement = (window.google as any)?.maps?.maps3d?.Map3DElement;
    if (!Map3DElement) return;

    const mapEl = new Map3DElement({
      center: { lat: center[1], lng: center[0], altitude },
      range: zoomToRange(zoom, center[1]),
      tilt: pitch,
      heading: bearing,
      // SATELLITE = 3D limpio sin labels ni POIs (decisión heredada de
      // mirall/viewport.js:275-277 — más legible para presentaciones).
      mode: 'SATELLITE',
    });
    mapEl.style.display = 'block';
    mapEl.style.width = '100%';
    mapEl.style.height = '100%';
    containerRef.current.appendChild(mapEl as Node);
    mapRef.current = mapEl as HTMLElement;

    // Propagar los cambios de cámara al callback compartido con MapView.
    // Map3DElement dispara `gmp-centerchange`, `gmp-rangechange`,
    // `gmp-tiltchange`, `gmp-headingchange` cuando el usuario interactúa.
    // No traen `detail` útil — los valores actuales se leen del propio
    // elemento.
    const report = () => {
      const cb = onViewStateRef.current;
      if (!cb) return;
      const c = (mapEl as any).center;
      if (!c) return;
      const lat = typeof c.lat === 'function' ? c.lat() : c.lat;
      const lng = typeof c.lng === 'function' ? c.lng() : c.lng;
      const range = (mapEl as any).range ?? 0;
      const tilt = (mapEl as any).tilt ?? 0;
      const heading = (mapEl as any).heading ?? 0;
      cb([lng, lat], rangeToZoom(range, lat), heading, tilt);
    };
    const events = ['gmp-centerchange', 'gmp-rangechange', 'gmp-tiltchange', 'gmp-headingchange'];
    events.forEach(ev => mapEl.addEventListener(ev, report));
    // Snapshot inicial — para que el overlay debug muestre algo desde el
    // primer momento, no sólo tras el primer drag.
    setTimeout(report, 200);

    // El padre monta sus markers cuando el mapa está listo. Devuelve
    // opcionalmente un cleanup que se ejecuta antes de remover el mapEl.
    let mapReadyCleanup: (() => void) | void;
    if (onMapReadyRef.current) {
      mapReadyCleanup = onMapReadyRef.current(mapEl as HTMLElement);
    }

    // WebSocket al camera-server: equivalente al CameraController de
    // MapLibre, pero aplicando `fly_to` al Map3DElement. Soporta:
    //   - Comando del CommandConsole (tecla 'i').
    //   - Encuadre automático de alarmas y demo tour.
    // El campo `zoom` MapLibre se convierte a `range` Map3D con la curva
    // calibrada (ZOOM_RANGE_CONST). En modo manual ('c') el operador
    // controla la cámara — ignoramos el fly_to pero seguimos delegando
    // la narrativa/alarma al padre (vía evento custom para no acoplar).
    //
    // Reconexión:
    //   - Backoff arranca en 500 ms (tras deploy, el container del
    //     camera-server se recrea en ~2 s — queremos reanudar rápido).
    //   - `disposed` corta el ciclo si el componente se desmonta mientras
    //     el timer estaba pendiente (evita las fugas de WS que vimos en
    //     los logs del server tras cambios de vista repetidos).
    let ws: WebSocket | null = null;
    let wsReconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let wsBackoff = 500;
    let disposed = false;
    function connectCameraWS() {
      if (disposed) return;
      ws = new WebSocket(CAMERA_WS_URL);
      ws.onopen = () => { wsBackoff = 500; };
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (!data || data.type === 'welcome') return;
          // Los comandos `source: 'manual'` (tecla I) se aplican siempre,
          // incluso en modo manual. Los fly_to de alarmas/demo respetan el
          // toggle `autoMove`.
          const cc = useCameraControl.getState();
          const manualOverride = Date.now() < cc.manualUntil;
          const allowFly = data.source === 'manual' || (cc.autoMove && !manualOverride);
          if (data.type === 'fly_to' && allowFly) {
            const m = mapEl as any;
            if (data.center) {
              m.center = {
                lat: data.center[1],
                lng: data.center[0],
                altitude: m.center?.altitude ?? altitude,
              };
            }
            if (data.zoom != null) {
              const lat = data.center ? data.center[1] : (m.center?.lat ?? 41.45);
              m.range = zoomToRange(data.zoom, lat);
            }
            if (data.pitch != null) m.tilt = data.pitch;
            if (data.bearing != null) m.heading = data.bearing;
          }
        } catch { /* swallow */ }
      };
      ws.onclose = () => {
        if (disposed) return;
        const d = wsBackoff;
        wsBackoff = Math.min(d * 2, 30000);
        wsReconnectTimer = setTimeout(connectCameraWS, d);
      };
      ws.onerror = () => {
        // Cerrar de inmediato — algunos navegadores demoran `close` tras
        // error de red y los comandos siguen perdiéndose. El `onclose`
        // posterior dispara la reconexión con backoff.
        try { ws?.close(); } catch { /* swallow */ }
      };
    }
    connectCameraWS();

    return () => {
      if (typeof mapReadyCleanup === 'function') {
        try { mapReadyCleanup(); } catch { /* swallow */ }
      }
      disposed = true;
      if (wsReconnectTimer) clearTimeout(wsReconnectTimer);
      ws?.close();
      events.forEach(ev => mapEl.removeEventListener(ev, report));
      mapEl.remove();
      mapRef.current = null;
    };
  }, [loaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sincronizar cámara cuando cambian las props (sin recrear el mapa).
  useEffect(() => {
    const mapEl = mapRef.current as any;
    if (!mapEl) return;
    mapEl.center = { lat: center[1], lng: center[0], altitude };
    mapEl.range = zoomToRange(zoom, center[1]);
    mapEl.tilt = pitch;
    mapEl.heading = bearing;
  }, [center, zoom, bearing, pitch, altitude]);

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-950">
        <div className="max-w-md text-center px-6 py-8 bg-gray-900/80 border border-amber-700/40 rounded-lg">
          <div className="text-amber-400 font-medium mb-2">Vista realista no disponible</div>
          <div className="text-xs text-gray-400">{error}</div>
          <div className="text-xs text-gray-500 mt-3">
            Pulsa el botón del header para volver a la vista operativa.
          </div>
        </div>
      </div>
    );
  }

  if (!loaded) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-950 text-gray-400 text-sm">
        Cargando vista 3D realista…
      </div>
    );
  }

  return <div ref={containerRef} className="w-full h-full" />;
}
